/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import { useCallback, useState, useRef } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { TextBorderLineJoin } from '@src/web-stories-wp/story-editor/src/elements/types';
import {
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  Icons,
  NumericInput,
  THEME_CONSTANTS,
  ToggleButton,
  useKeyDownEffect,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useConfig } from '../../../../app';
import Tooltip from '../../../tooltip';
import {
  Label,
  type MultipleValue,
  SettingsRow,
  SettingsRowInputWrapper,
} from './shared';
import { focusStyle, inputContainerStyleOverride } from '../../shared';

const StrokeLineJoinRow = styled.div.attrs({
  role: 'listbox',
})`
  display: flex;
  align-items: center;
`;

const StrokeLineJoinButton = styled(ToggleButton).attrs({
  variant: BUTTON_VARIANTS.SQUARE,
  size: BUTTON_SIZES.SMALL,
})`
  ${focusStyle}
`;

const strokeLineJoinButtonIds = [
  'miterLineJoin',
  'bevelLineJoin',
  'roundLineJoin',
];

const MIN_MITER_LIMIT_IN_DEGREES = 7.17; // From Figma
const MAX_MITER_LIMIT_IN_DEGREES = 180;

const MIN_MITER_LIMIT = 1; // The 180° Figma maximum, converted
const MAX_MITER_LIMIT = 16; // The 7.17° Figma minimum, converted

type StrokeLineJoinButtonId = typeof strokeLineJoinButtonIds[number];

type TextBorderStrokeLineJoinSettingsProps = {
  lineJoin: TextBorderLineJoin | MultipleValue;
  miterLimit: number;
  handleChange: (
    property: 'lineJoin' | 'miterLimit',
    value: TextBorderLineJoin | number
  ) => void;
};

export function TextBorderStrokeLineJoinSettings({
  lineJoin,
  miterLimit,
  handleChange,
}: TextBorderStrokeLineJoinSettingsProps) {
  const { isRTL } = useConfig();

  const rowRef = useRef<HTMLDivElement>();
  const [currentButton, setCurrentButton] =
    useState<StrokeLineJoinButtonId | null>(null);

  const handleStrokeLineJoinButtonNavigation = useCallback(
    (direction) => () => {
      const currentIndex = strokeLineJoinButtonIds.findIndex(
        (id) => id === currentButton
      );
      const nextButtonId = strokeLineJoinButtonIds[currentIndex + direction];
      if (!nextButtonId) {
        return;
      }
      setCurrentButton(nextButtonId);
      rowRef.current.querySelector(`#${nextButtonId}`).focus();
    },
    [currentButton, setCurrentButton]
  );

  useKeyDownEffect(
    rowRef,
    'left',
    () => handleStrokeLineJoinButtonNavigation(isRTL ? 1 : -1),
    [handleStrokeLineJoinButtonNavigation, isRTL]
  );

  useKeyDownEffect(
    rowRef,
    'right',
    () => handleStrokeLineJoinButtonNavigation(isRTL ? -1 : 1),
    [handleStrokeLineJoinButtonNavigation, isRTL]
  );

  return (
    <>
      <SettingsRow>
        <Label size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
          {__('Join', 'web-stories')}
        </Label>
        <StrokeLineJoinRow ref={rowRef}>
          <Tooltip title={__('Miter', 'web-stories')}>
            <StrokeLineJoinButton
              aria-label={__('Miter', 'web-stories')}
              aria-selected={lineJoin === TextBorderLineJoin.Miter}
              aria-pressed={lineJoin === TextBorderLineJoin.Miter}
              id={strokeLineJoinButtonIds[0]}
              isToggled={lineJoin === TextBorderLineJoin.Miter}
              onClick={() => handleChange('lineJoin', TextBorderLineJoin.Miter)}
              onFocus={() => setCurrentButton(strokeLineJoinButtonIds[0])}
            >
              <Icons.StrokeLineJoinMiter />
            </StrokeLineJoinButton>
          </Tooltip>
          <Tooltip title={__('Bevel', 'web-stories')}>
            <StrokeLineJoinButton
              aria-label={__('Bevel', 'web-stories')}
              aria-selected={lineJoin === TextBorderLineJoin.Bevel}
              aria-pressed={lineJoin === TextBorderLineJoin.Bevel}
              id={strokeLineJoinButtonIds[1]}
              isToggled={lineJoin === TextBorderLineJoin.Bevel}
              onClick={() => handleChange('lineJoin', TextBorderLineJoin.Bevel)}
              onFocus={() => setCurrentButton(strokeLineJoinButtonIds[1])}
            >
              <Icons.StrokeLineJoinBevel />
            </StrokeLineJoinButton>
          </Tooltip>
          <Tooltip title={__('Round', 'web-stories')}>
            <StrokeLineJoinButton
              aria-label={__('Round', 'web-stories')}
              aria-selected={lineJoin === TextBorderLineJoin.Round}
              aria-pressed={lineJoin === TextBorderLineJoin.Round}
              id={strokeLineJoinButtonIds[2]}
              isToggled={lineJoin === TextBorderLineJoin.Round}
              onClick={() => handleChange('lineJoin', TextBorderLineJoin.Round)}
              onFocus={() => setCurrentButton(strokeLineJoinButtonIds[2])}
            >
              <Icons.StrokeLineJoinRound />
            </StrokeLineJoinButton>
          </Tooltip>
        </StrokeLineJoinRow>
      </SettingsRow>
      {lineJoin === TextBorderLineJoin.Miter && (
        <SettingsRow>
          <Label size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('Miter limit', 'web-stories')}
          </Label>
          <SettingsRowInputWrapper>
            <NumericInput
              aria-label={__('Miter limit', 'web-stories')}
              allowEmpty={false}
              isFloat
              min={MIN_MITER_LIMIT_IN_DEGREES}
              max={MAX_MITER_LIMIT_IN_DEGREES}
              value={miterLimitToDegrees(miterLimit)}
              onChange={(event, value) => {
                handleChange('miterLimit', degreesToMiterLimit(value));
              }}
              onBlur={(event, value) => {
                handleChange('miterLimit', degreesToMiterLimit(value));
              }}
              containerStyleOverride={inputContainerStyleOverride}
              unit="°"
            />
          </SettingsRowInputWrapper>
        </SettingsRow>
      )}
    </>
  );
}

const RADIANS_TO_DEGREES = 180 / Math.PI;
const DEGREES_TO_RADIANS = Math.PI / 180;

/**
 * In the Figma UI, the miter limit is displayed in degrees.
 * Even though it's a unitless number in the SVG spec, we convert it to degrees to display
 * in the input UI, to make it easier for our designers.
 */
function miterLimitToDegrees(miterLimit: number): number {
  const clampedMiterLimit = Math.max(MIN_MITER_LIMIT, miterLimit);

  // Guard against floating-point drift
  const inverse = Math.min(1, 1 / clampedMiterLimit);

  // Compute angle
  const angleInRadians = 2 * Math.asin(inverse);
  const angleInDegrees = angleInRadians * RADIANS_TO_DEGREES;

  // Figma allows up to 2 decimal places
  return Math.round(angleInDegrees * 100) / 100;
}

function degreesToMiterLimit(angleInDegrees: number): number {
  const clampedAngle = Math.min(
    MAX_MITER_LIMIT_IN_DEGREES,
    Math.max(MIN_MITER_LIMIT_IN_DEGREES, angleInDegrees)
  );

  // Avoid division by zero at 0°
  if (clampedAngle === 0) {
    return MAX_MITER_LIMIT;
  }

  const halfAngleInRadians = (clampedAngle * DEGREES_TO_RADIANS) / 2;

  const sine = Math.sin(halfAngleInRadians);

  // Guard against floating-point underflow
  if (sine <= 0) {
    return MAX_MITER_LIMIT;
  }

  const miterLimit = 1 / sine;

  return Math.max(MIN_MITER_LIMIT, miterLimit);
}
